'use client';

import { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { stylesService } from '@/features/design/render';
import type { Style } from '@/types/style';

interface GroupedStyles {
  contemporary: Style[];
  traditional: Style[];
}

// Global cache for styles data (locale-aware)
let stylesCache: {
  data: Style[] | null;
  groupedData: GroupedStyles | null;
  timestamp: number;
  locale: string | null;
  promise: Promise<Style[]> | null;
} = {
  data: null,
  groupedData: null,
  timestamp: 0,
  locale: null,
  promise: null,
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useStyles() {
  const locale = useLocale();
  const [styles, setStyles] = useState<Style[]>(stylesCache.data || []);
  const [groupedStyles, setGroupedStyles] = useState<GroupedStyles>(
    stylesCache.groupedData || {
      contemporary: [],
      traditional: [],
    }
  );
  const [isLoading, setIsLoading] = useState(!stylesCache.data);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStyles = async () => {
      // Check if cache is still valid and locale matches
      const now = Date.now();
      if (
        stylesCache.data &&
        stylesCache.groupedData &&
        stylesCache.locale === locale &&
        now - stylesCache.timestamp < CACHE_DURATION
      ) {
        // Use cached data
        setStyles(stylesCache.data);
        setGroupedStyles(stylesCache.groupedData);
        setIsLoading(false);
        return;
      }

      // If there's already a fetch in progress, wait for it
      if (stylesCache.promise) {
        try {
          setIsLoading(true);
          const response = await stylesCache.promise;

          // Use the cached grouped data (set by the first request)
          if (stylesCache.groupedData) {
            setStyles(response);
            setGroupedStyles(stylesCache.groupedData);
          }
        } catch (err) {
          setError(
            err instanceof Error ? err.message : 'Failed to fetch styles'
          );
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Make new API call
      try {
        setIsLoading(true);

        // Store promise in cache to prevent concurrent requests
        stylesCache.promise = stylesService.getStyles();
        const response = await stylesCache.promise;

        // Group styles by type
        const grouped = response.reduce<GroupedStyles>(
          (acc, style) => {
            if (style.attributes.type === 'contemporary') {
              acc.contemporary.push(style);
            } else {
              acc.traditional.push(style);
            }
            return acc;
          },
          { contemporary: [], traditional: [] }
        );

        // Update cache
        stylesCache = {
          data: response,
          groupedData: grouped,
          timestamp: now,
          locale,
          promise: null,
        };

        setStyles(response);
        setGroupedStyles(grouped);
      } catch (err) {
        // Clear promise on error
        stylesCache.promise = null;
        setError(err instanceof Error ? err.message : 'Failed to fetch styles');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStyles();
  }, [locale]);

  return { styles, groupedStyles, isLoading, error };
}
